import { useMemo, useSyncExternalStore } from 'react';
import {
  fetchManagedAgentProvisioningStatus,
  type ManagedAgentProvisioningStatusDTO,
} from '../lib/managedAgentProvisioningStatus';
import {
  hasPendingManagedAgentProvisioningMutations,
  subscribeManagedAgentProvisioningMutations,
} from '../lib/managedAgentProvisioningMutations';
import { useAuthStore } from '../stores/useAuthStore';
import { usePresenceStore } from '../stores/usePresenceStore';

interface ProvisioningSnapshot {
  status: ManagedAgentProvisioningStatusDTO | null;
  readState: 'loading' | 'ready' | 'error' | 'disconnected';
  receivedAt: number;
  now: number;
  refreshing: boolean;
}

export interface ManagedAgentProvisioningStatusSubscription extends ProvisioningSnapshot {
  refresh: () => void;
}

interface Subscription {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ProvisioningSnapshot;
  refresh: () => void;
}

const emptySnapshot: ProvisioningSnapshot = {
  status: null,
  readState: 'loading',
  receivedAt: 0,
  now: 0,
  refreshing: false,
};
const subscriptions = new Map<string, Subscription>();
const inactiveSubscription: Subscription = {
  subscribe: () => () => {},
  getSnapshot: () => emptySnapshot,
  refresh: () => {},
};

const createSubscription = (key: string, agentId: string, ownerId: string): Subscription => {
  const listeners = new Set<() => void>();
  let snapshot = emptySnapshot;
  let generation = 0;
  let pendingRefresh = false;
  let refreshScheduled = false;
  let request: AbortController | null = null;
  let requestDeadline: ReturnType<typeof setTimeout> | undefined;
  let stopObserving: (() => void) | undefined;

  const authorized = () => {
    const user = useAuthStore.getState().currentUser;
    return user?.id === ownerId && user.role === 'member';
  };
  const connected = () => usePresenceStore.getState().connectionState === 'connected';
  const active = () => listeners.size > 0
    && authorized()
    && document.visibilityState !== 'hidden'
    && !hasPendingManagedAgentProvisioningMutations();

  const publish = (next: ProvisioningSnapshot) => {
    snapshot = next;
    for (const listener of listeners) listener();
  };
  const clear = () => publish({
    ...emptySnapshot,
    now: performance.now(),
    readState: connected() ? 'loading' : 'disconnected',
  });
  const invalidate = () => {
    generation += 1;
    pendingRefresh = false;
    request?.abort();
    clearTimeout(requestDeadline);
    clear();
  };
  const releaseIfUnused = () => {
    // Keep an aborted transport attached until it settles, including remounts.
    if (listeners.size === 0 && request === null && subscriptions.get(key) === subscription) {
      subscriptions.delete(key);
    }
  };

  const read = async () => {
    if (!active() || request !== null || !pendingRefresh) return;
    pendingRefresh = false;
    const controller = new AbortController();
    const readGeneration = generation;
    request = controller;
    publish({ ...snapshot, refreshing: true, now: performance.now() });
    const deadline = setTimeout(() => {
      controller.abort();
      if (generation === readGeneration && active()) {
        publish({
          ...emptySnapshot,
          now: performance.now(),
          readState: connected() ? 'error' : 'disconnected',
        });
      }
    }, 10_000);
    requestDeadline = deadline;
    try {
      const status = await fetchManagedAgentProvisioningStatus(agentId, controller.signal);
      if (controller.signal.aborted || generation !== readGeneration || !active()) return;
      const now = performance.now();
      publish({ status, readState: connected() ? 'ready' : 'disconnected', receivedAt: now, now, refreshing: false });
    } catch {
      if (controller.signal.aborted || generation !== readGeneration || !active()) return;
      publish({ ...emptySnapshot, now: performance.now(), readState: connected() ? 'error' : 'disconnected' });
    } finally {
      clearTimeout(deadline);
      requestDeadline = undefined;
      request = null;
      releaseIfUnused();
      if (pendingRefresh) scheduleRefresh();
    }
  };

  const scheduleRefresh = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    queueMicrotask(() => {
      refreshScheduled = false;
      void read();
    });
  };
  const refresh = () => {
    if (!active()) return;
    pendingRefresh = true;
    scheduleRefresh();
  };

  const observe = () => {
    const visibilityChanged = () => {
      invalidate();
      refresh();
    };
    const unsubscribeAuth = useAuthStore.subscribe((state, previous) => {
      if (state.currentUser === previous.currentUser) return;
      invalidate();
      refresh();
    });
    const unsubscribeConnection = usePresenceStore.subscribe((state, previous) => {
      if (state.connectionState === previous.connectionState) return;
      invalidate();
      refresh();
    });
    // A computer's assignment includes every placed Agent, so one mutation can
    // invalidate another Agent's setup even when its own configuration is unchanged.
    const unsubscribeMutations = subscribeManagedAgentProvisioningMutations(() => {
      invalidate();
      refresh();
    });
    document.addEventListener('visibilitychange', visibilityChanged);
    window.addEventListener('focus', refresh);
    const polling = setInterval(refresh, 3_000);
    const clock = setInterval(() => {
      if (active() && snapshot.status !== null) publish({ ...snapshot, now: performance.now() });
    }, 1_000);
    stopObserving = () => {
      unsubscribeAuth();
      unsubscribeConnection();
      unsubscribeMutations();
      document.removeEventListener('visibilitychange', visibilityChanged);
      window.removeEventListener('focus', refresh);
      clearInterval(polling);
      clearInterval(clock);
    };
    clear();
    refresh();
  };

  const subscription: Subscription = {
    getSnapshot: () => snapshot,
    refresh,
    subscribe: (listener) => {
      subscriptions.set(key, subscription);
      listeners.add(listener);
      if (listeners.size === 1) observe();
      return () => {
        listeners.delete(listener);
        if (listeners.size !== 0) return;
        stopObserving?.();
        stopObserving = undefined;
        invalidate();
        releaseIfUnused();
      };
    },
  };
  return subscription;
};

export const useManagedAgentProvisioningStatus = (
  agentId: string,
  ownerId: string | null,
  enabled = true,
): ManagedAgentProvisioningStatusSubscription => {
  const userId = useAuthStore((state) => state.currentUser?.id);
  const role = useAuthStore((state) => state.currentUser?.role);
  const subscription = useMemo(() => {
    if (!enabled || ownerId === null || userId !== ownerId || role !== 'member') return inactiveSubscription;
    const key = JSON.stringify([userId, agentId]);
    const existing = subscriptions.get(key);
    if (existing) return existing;
    const created = createSubscription(key, agentId, ownerId);
    subscriptions.set(key, created);
    return created;
  }, [agentId, ownerId, userId, role, enabled]);
  const snapshot = useSyncExternalStore(subscription.subscribe, subscription.getSnapshot);
  return { ...snapshot, refresh: subscription.refresh };
};
